import React, {Component} from 'react';
import SalesComparisonReportsFilter from '../components/SalesComparisonReportsFilter';
import SalesComparisonReport from '../components/SalesComparisonReport';
import NoAccess from '../noAccess';
import {hasReportsAccess} from '../shared/authFunctions';
import {convertDate} from "../shared/functions";
import {getTimeZoneOffsetDefault} from '../shared/dateTimeLists';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardText} from 'material-ui/Card';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/compare-arrows';

export const fuseSalesComparisonReportsIcon = Icon;

/**
 * Sales Comparison Reports class for retrieving and displaying summary
 *
 */
export class SalesComparisonReports extends Component {
    constructor(props) {
        super(props);
        const system = localStorage.getItem('system');
        const offsetDefault = getTimeZoneOffsetDefault();
        this.state = {
            detailComplete: false,
            errorMessage: null,
            errorMessageLocation: null,
            location: {
                id: null,
                name: '',
                city: '',
                state: ''
            },
            baseStartDate: null,
            baseEndDate: null,
            compareStartDate: null,
            compareStartDateObj: null,
            compareEndDate: null,
            compareEndDateObj: null,
            selectedOffset: offsetDefault,
            summaryComplete: false,
            system: system,
            yearsList: []
        };
    }

    /**
     * Convert Dates to visual display
     *
     * @param start
     * @param end
     * @return {string}
     */
    displaySelectedDates(start, end) {
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        let startDate = new Date(start + 'T00:00:00');
        let endDate = new Date(end + 'T00:00:00');
        let startDateStr = startDate.toLocaleDateString('en-US', options);
        let endDateStr = endDate.toLocaleDateString('en-US', options);

        return (
            startDateStr + ' through ' + endDateStr
        )
    }

    /**
     * Assemble an array of dates to retrieve data for
     *
     * @param start
     * @param end
     * @return {{num: number, start: *, end: *, label: string}[]}
     */
    createDateCollections(start, end) {
        const baseStart = this.state.baseStartDate;
        const baseEnd = this.state.baseEndDate;
        let baseYear = baseStart.split('-')[0];
        let compareYear = start.split('-')[0];
        let yearTotal = (baseYear - compareYear);
        // Create a collection beginning with the base year
        let collection = [
            {
                label: 'base',
                num: 0,
                start: baseStart,
                end: baseEnd
            }
        ];
        for (let i = 1; i <= yearTotal; i++) {
            // Create a date for each year
            let sDate = new Date(baseStart);
            let eDate = new Date(baseEnd);
            sDate.setMinutes(sDate.getMinutes() + sDate.getTimezoneOffset());
            eDate.setMinutes(eDate.getMinutes() + eDate.getTimezoneOffset());
            // Subtract years for each date
            sDate.setMonth(sDate.getMonth() - (12 * i));
            eDate.setMonth(eDate.getMonth() - (12 * i));
            let sStr = convertDate(sDate, 'query', false);
            let eStr = convertDate(eDate, 'query', false);
            // Create an object to store
            const year = {
                label: 'compare-' + i,
                num: i,
                start: sStr,
                end: eStr
            };
            collection.push(year);
        }
        return collection;
    }

    /**
     * Handle event props from SalesComparisonReportsFilter
     *
     * @param type
     * @param value
     */
    handleFilterSelect = (type, value) => {
        switch (type) {
            case 'start_date':
                // Set Start Date selection for reports summary component
                this.setState({
                    baseStartDate: value,
                    yearsList: []
                });
                break;
            case 'end_date':
                // Set End selection for reports summary component
                this.setState({
                    baseEndDate: value,
                    yearsList: []
                });
                break;
            case 'offset':
                // Set Time Zone Offset selection for reports summary component
                this.setState({
                    selectedOffset: value,
                    yearsList: []
                });
                break;
            case 'compare_dates':
                // Split Compare Dates into set
                const dateArr = value.split('_');
                if (dateArr && dateArr.length > 1) {
                    const years = this.createDateCollections(dateArr[0], dateArr[1]);
                    this.setState({
                        compareStartDate: dateArr[0],
                        compareEndDate: dateArr[1],
                        yearsList: years
                    });
                }
                break;
            default:
                return
        }
    };

    /**
     * Handle event props for Sales Comparison (All Locations) table completion
     *
     * @param isComplete
     */
    handleSummaryComplete = (isComplete) => {
        this.setState({
            summaryComplete: isComplete,
        });
    };

    handleReportError = (message, type) => {
        let stateObj = (type === 'location') ? {
            errorMessageLocation: message,
            summaryComplete: true
        } : {
            errorMessage: message,
            summaryComplete: true
        };
        this.setState(stateObj);
    };

    /**
     * Handle event props for Sales Comparison (Location) table completion
     *
     * @param status
     */
    handleDetailUpdate = (status) => {
        this.setState({
            detailComplete: (status === 'complete')
        });
    };

    render() {
        const {
            errorMessage,
            errorMessageLocation,
            baseStartDate,
            baseEndDate,
            selectedOffset,
            compareStartDate,
            system,
            yearsList
        } = this.state;

        if (!hasReportsAccess()) return <NoAccess/>;

        const ListTitle = <span className="list-title hide-print">
            <Avatar icon={<Icon/>} className="avatar-title"/>Sales Comparison Reports</span>;

        const ListDescription = () => {
            return (
                <span className="list-description hide-print">
                    <p>A Sales Comparison Report displays gross and net totals and average orders for all Locations for
                        a specified date range. Selecting a Location displays totals and averages by that Location.</p>
                    <p>Select a base Date Range and Time Zone Offset. Then select the desired set of years back to
                        compare each year's collection of Gross totals, Net totals, and Average sales totals.</p>
                    <p>Click any Location in the results to view all values by date for that Location.</p>
                </span>
            );
        };

        const prevYears = yearsList.length - 1;

        return (
            <div className="lists-container">
                <Card>
                    <ViewTitle title={ListTitle}/>
                    <CardText>
                        <ListDescription/>

                        {/* Report Filter (Start Date, End Date, Time Zone) */}
                        <SalesComparisonReportsFilter
                            minCompareYear={2010}
                            onSelectStartDate={this.handleFilterSelect}
                            onSelectEndDate={this.handleFilterSelect}
                            onSelectOffset={this.handleFilterSelect}
                            onSelectCompareDates={this.handleFilterSelect}
                        />

                        {/* Sales Comparison Report Content */}
                        {baseStartDate && baseEndDate && selectedOffset && compareStartDate && yearsList &&
                        <div>

                            {/* Report Header and messaging */}
                            <div className="report-header">
                                <h2 className="report-header-title">Sales Comparison Report</h2>
                                <p>
                                    <strong>Selected Range: </strong>
                                    {this.displaySelectedDates(baseStartDate, baseEndDate)}
                                </p>
                                <p>
                                    <strong>Previous Years Compared: </strong>
                                    {prevYears}
                                </p>
                            </div>

                            {/* Summary Report */}
                            <SalesComparisonReport
                                yearsList={yearsList}
                                startDate={baseStartDate}
                                endDate={baseEndDate}
                                offset={selectedOffset}
                                onDetailUpdate={this.handleDetailUpdate}
                                onError={this.handleReportError}
                                onSummaryComplete={this.handleSummaryComplete}
                                system={system}
                            />

                        </div>
                        }

                        {/* Error and Messaging */}
                        {errorMessage &&
                            <div className="error">
                                <p>{errorMessage}</p>
                            </div>
                        }
                        {/* Error and Messaging */}
                        {errorMessageLocation &&
                            <div className="error">
                                <p>{errorMessageLocation}</p>
                            </div>
                        }
                    </CardText>
                </Card>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/Report/SalesComparisonReports.js